import grapesjs from 'grapesjs';
import grapesjsmjml from 'grapesjs-mjml';
import grapesjsnewsletter from 'grapesjs-preset-newsletter';
import grapesjswebpage from 'grapesjs-preset-webpage';
import grapesjspostcss from 'grapesjs-parser-postcss';
import contentService from 'grapesjs-preset-mautic/dist/content.service';
import grapesjsmautic from 'grapesjs-preset-mautic';
import mjmlService from 'grapesjs-preset-mautic/dist/mjml/mjml.service';
import editorFontsService from 'grapesjs-preset-mautic/dist/editorFonts/editorFonts.service';
// CKEditor plugin removed to enable native inline editing
// import 'grapesjs-plugin-ckeditor5';

// for local dev
// import contentService from '../../../../../../grapesjs-preset-mautic/src/content.service';
// import grapesjsmautic from '../../../../../../grapesjs-preset-mautic/src';
// import mjmlService from '../../../../../../grapesjs-preset-mautic/src/mjml/mjml.service';

import CodeModeButton from './codeMode/codeMode.button';
import GrapesJSTheme from './grapesjs-theme';
import BlockIcons from './grapesjs-block-icons';
import CanvasImprovements from './grapesjs-canvas-improvements';
import KeyboardShortcuts from './grapesjs-keyboard';
import initGrapesJSUXEnhancements from './grapesjs-ux-interactions';
import initEnhancedUXImprovements from './grapesjs-ux-improvements-enhanced';
import PlaceholderHelper from './placeholderHelper/placeholderHelper';

export default class BuilderService {
  editor;

  assets;

  uploadPath;

  deletePath;

  theme;

  canvasImprovements;

  keyboardShortcuts;

  /**
   * @param {*} assets
   */
  constructor(assets) {
    if (!assets.conf.uploadPath) {
      throw Error('No uploadPath found');
    }
    if (!assets.conf.deletePath) {
      throw Error('No deletePath found');
    }

    this.assets = assets.files;
    this.uploadPath = assets.conf.uploadPath;
    this.deletePath = assets.conf.deletePath;
  }

  /**
   * Initialize GrapesJsBuilder
   *
   * @param object
   */
  setListeners() {
    if (!this.editor) {
      throw Error('No editor found');
    }

    const keymaps = this.editor.Keymaps;
    let allKeymaps;

    this.editor.on('modal:open', () => {
      // Save all keyboard shortcuts
      allKeymaps = { ...keymaps.getAll() };

      // Remove keyboard shortcuts to prevent launch behind popup
      keymaps.removeAll();
    });

    this.editor.on('modal:close', () => {
      // ReMap keyboard shortcuts on modal close
      Object.keys(allKeymaps).map((objectKey) => {
        const shortcut = allKeymaps[objectKey];

        keymaps.add(shortcut.id, shortcut.keys, shortcut.handler);
        return keymaps;
      });
    });

    this.editor.on('asset:remove', (response) => {
      // Delete file on server
      mQuery.ajax({
        url: this.deletePath,
        data: { filename: response.getFilename() },
      });
    });
  }

  /**
   * Initialize the grapesjs build in the
   * correct mode
   */
  initGrapesJS(object) {
    // disable mautic global shortcuts
    Mousetrap.reset();
    if (object === 'page') {
      this.editor = this.initPage();
    } else if (object === 'emailform') {
      if (mjmlService.getOriginalContentMjml()) {
        this.editor = this.initEmailMjml();
      } else {
        this.editor = this.initEmailHtml();
      }
    } else {
      throw Error(`Not supported builder type: ${object}`);
    }

    // add code mode button
    // @todo: only show button if configured: sourceEdit: 1,
    const codeModeButton = new CodeModeButton(this.editor);
    codeModeButton.addCommand();
    codeModeButton.addButton();

    if (mauticEditorFonts) {
      this.editor.on('load', () => editorFontsService.loadEditorFonts(this.editor));
    }

    this.enableInlineEditing();
    this.setListeners();
    this.initTheme();
    this.initBlockIcons();
    this.initCanvasImprovements();
    this.initKeyboardShortcuts();
    this.initPlaceholderHelper();
    this.initUXEnhancements();
    this.initCustomStyleProperties();
    this.initCloseDiscardsChanges();
    this.initApplySegmentGate();
  }

  /**
   * Make close/cancel commands discard changes instead of persisting them.
   *
   * The default grapesjs-preset-mautic close commands extract the current
   * editor content and write it to the builder textareas before hiding the
   * UI.  That means the changed content ends up in the form even though the
   * user clicked "Close" (not "Apply").  The next form save then persists
   * the unintended changes.
   *
   * This method snapshots the textarea values when the builder opens and
   * overrides every close command to restore those originals.
   */
  initCloseDiscardsChanges() {
    if (!this.editor) return;

    // Snapshot the textarea values that existed before the editor loaded.
    // This snapshot is the "last persisted" content. Close restores it so
    // unapplied edits are discarded. Apply refreshes it so applied edits
    // become the new baseline (otherwise Close would revert Apply's work).
    const snapshot = {
      html: mQuery('textarea.builder-html').val() || '',
      mjml: mQuery('textarea.builder-mjml').val() || '',
    };

    const closeCmds = [
      'mautic-editor-email-mjml-close',
      'mautic-editor-email-html-close',
      'mautic-editor-page-html-close',
    ];

    const editor = this.editor;

    const overrideCloseCommands = () => {
      closeCmds.forEach((cmdName) => {
        if (!editor.Commands.has(cmdName)) return;

        editor.Commands.add(cmdName, {
          run() {
            // Restore the snapshot (discard any unapplied editor changes)
            mQuery('textarea.builder-html').val(snapshot.html);
            mQuery('textarea.builder-mjml').val(snapshot.mjml);

            // Hide the builder UI — mirrors ButtonCloseCommands.resetHtml()
            mQuery('.builder').removeClass('builder-active').addClass('hide');
            mQuery('html').css('font-size', '');
            mQuery('body').css('overflow-y', '');
            mQuery('.builder-panel').css('display', 'none');
          },
        });
      });
    };

    // Override immediately if the commands are already registered (editor may
    // have loaded synchronously during initEmailMjml/initEmailHtml/initPage).
    overrideCloseCommands();

    // Also listen for the load event in case the commands are registered later.
    editor.on('load', overrideCloseCommands);

    // Rebaseline the snapshot to whatever is currently staged in the textareas.
    const refreshSnapshot = () => {
      snapshot.html = mQuery('textarea.builder-html').val() || '';
      snapshot.mjml = mQuery('textarea.builder-mjml').val() || '';
    };

    // Refresh snapshot after Apply runs — Apply has just written the editor
    // content to the textareas and POSTed it to the server. Without this,
    // a subsequent Close would restore the pre-Apply textarea values and the
    // next form save would overwrite the freshly-persisted DB content
    // (symptom: add image → Apply → Close → reopen builder → the edit is gone).
    // GrapesJS 0.22 emits `command:run:<id>`; older builds emitted the bare
    // `run:<id>`. Listen for BOTH (space-separated) so a version bump that
    // changes the event name can never silently disable this rebaseline again.
    editor.on('run:preset-mautic:apply-form command:run:preset-mautic:apply-form', refreshSnapshot);

    // The "Edit code" editor's Save stages its content to the textareas without a
    // full Apply (see EnhancedCodeEditor.stageContentToForm). Rebaseline here too so
    // a subsequent Close keeps that saved code instead of discarding it — otherwise
    // code-editor edits are silently lost on close.
    editor.on('mautic-code-editor:applied', refreshSnapshot);
  }

  /**
   * Let the builder's Apply (✓) button save content even when a segment email
   * has no segment selected, and surface a helpful hint instead of a silently
   * greyed-out button.
   *
   * Upstream grapesjs-preset-mautic (buttons/buttonApply.js) hard-disables Apply
   * whenever emailType === 'list' and no segment is chosen — which blocks *saving
   * the design* even though a segment is only actually required at *send* time.
   * Worse, the fork's own tooltip normalizer (grapesjs-ux-interactions.js) rewrites
   * the disabled button's tooltip back to "Apply Changes", erasing the one clue as
   * to why it was disabled. Marketers just saw a dead checkmark with no explanation.
   *
   * Here we re-enable Apply so content always saves, and show a dismissible in-builder
   * hint reminding the marketer to pick a segment before sending. The segment field
   * lives on the (hidden) email form and cannot change while the builder is open, so
   * the state is evaluated once on open.
   */
  initApplySegmentGate() {
    if (!this.editor) return;

    const editor = this.editor;

    const isSegmentEmailWithoutList = () => {
      const typeEl = document.getElementById('emailform_emailType');
      if (!typeEl || typeEl.value !== 'list') return false;
      const listsEl = document.getElementById('emailform_lists');
      if (!listsEl) return false;
      return [...listsEl.options].filter((o) => o.selected).length === 0;
    };

    // (B) Re-enable Apply if upstream disabled it purely for a missing segment.
    // The guard (disabled + no command) matches exactly the buttonApply.js branch,
    // so a normally-enabled Apply and non-segment emails are left untouched.
    const enableApply = () => {
      const btn = editor.Panels.getButton('views', 'views-apply');
      if (!btn) return;
      if (btn.get('disable') === true && !btn.get('command')) {
        btn.set('command', 'preset-mautic:apply-form');
        btn.set('disable', false);
      }
    };

    const HINT_ID = 'gjs-apply-segment-hint';

    const removeHint = () => {
      const el = document.getElementById(HINT_ID);
      if (el) el.remove();
    };

    // The helpful hint: a dismissible banner inside the builder overlay, shown only
    // for a segment email with no segment. Uses inline styles so it is self-contained
    // (no CSS/build coupling) and cannot be clobbered by the tooltip normalizer.
    const renderHint = () => {
      removeHint();
      if (!isSegmentEmailWithoutList()) return;

      const container = document.querySelector('.builder');
      if (!container) return;

      const banner = document.createElement('div');
      banner.id = HINT_ID;
      banner.setAttribute('role', 'status');
      banner.style.cssText = [
        'position:absolute',
        'top:60px',
        'left:50%',
        'transform:translateX(-50%)',
        'z-index:100000',
        'max-width:560px',
        'display:flex',
        'align-items:flex-start',
        'gap:10px',
        'padding:10px 14px',
        'background:#fff8e1',
        'border:1px solid #f0c36d',
        'border-radius:6px',
        'box-shadow:0 2px 8px rgba(0,0,0,0.15)',
        'font-size:13px',
        'line-height:1.4',
        'color:#5c4600',
      ].join(';');

      const icon = document.createElement('i');
      icon.className = 'fa fa-info-circle';
      icon.style.cssText = 'margin-top:1px;color:#c8901a';

      const text = document.createElement('span');
      text.style.flex = '1';
      // textContent (not innerHTML) — no untrusted interpolation.
      text.textContent = Mautic.translate('grapesjsbuilder.applyNoSegmentHint');
      // Fallback if the translation key is not present in the catalogue.
      if (text.textContent === 'grapesjsbuilder.applyNoSegmentHint') {
        text.textContent =
          'You can save your design now. This is a segment email — remember to choose a segment before sending it.';
      }

      const close = document.createElement('button');
      close.type = 'button';
      close.setAttribute('aria-label', 'Dismiss');
      close.textContent = '×';
      close.style.cssText =
        'border:none;background:none;font-size:16px;line-height:1;cursor:pointer;color:#5c4600;padding:0 2px';
      close.addEventListener('click', removeHint);

      banner.append(icon, text, close);
      container.append(banner);
    };

    const applyGateFixes = () => {
      enableApply();
      renderHint();
    };

    // Buttons are registered synchronously during initEmail*, so run immediately,
    // and again on load in case registration happens later.
    applyGateFixes();
    editor.on('load', applyGateFixes);
  }

  /**
   * Initialize placeholder helper panel
   */
  initPlaceholderHelper() {
    if (!this.editor) {
      return;
    }

    try {
      const placeholderHelper = new PlaceholderHelper(this.editor);
      placeholderHelper.init();
    } catch (error) {
      console.error('BuilderService: Failed to initialize PlaceholderHelper', error);
    }
  }

  /**
   * Initialize URL field in Typography section using custom approach
   */
  initCustomStyleProperties() {
    if (!this.editor) {
      return;
    }

    const editor = this.editor;

    // Try adding immediately (in case load event already fired)
    setTimeout(() => {
      this.addUrlFieldToTypography();
    }, 1000);

    // Also try on load event as backup
    editor.on('load', () => {
      setTimeout(() => {
        this.addUrlFieldToTypography();
      }, 500);
    });
  }

  /**
   * Add URL field to Typography section by injecting DOM element
   */
  addUrlFieldToTypography() {
    if (!this.editor) {
      console.error('No editor in addUrlFieldToTypography');
      return;
    }

    const editor = this.editor;

    // Check if field already exists
    if (document.getElementById('custom-url-field')) {
      return;
    }

    // Find Typography sector
    const sectors = document.querySelectorAll('.gjs-sm-sector');

    let typographySector = null;

    for (const sector of sectors) {
      const title = sector.querySelector('.gjs-sm-sector-title .gjs-sm-sector-label');
      if (title && title.textContent.trim() === 'Typography') {
        typographySector = sector;
        break;
      }
    }

    if (!typographySector) {
      console.error('Typography sector not found in DOM');
      return;
    }

    const propertiesContainer = typographySector.querySelector('.gjs-sm-properties');
    if (!propertiesContainer) {
      console.error('Typography properties container not found');
      return;
    }

    // Create URL field HTML with full width and open button
    const urlFieldHTML = `
      <div class="gjs-sm-property gjs-sm-text gjs-sm-property__href gjs-sm-property--full" id="custom-url-field" style="display: none;">
        <div class="gjs-sm-label" data-sm-label="">
          <span class="gjs-sm-icon" title="">URL</span>
          <div class="gjs-sm-clear" style="display: none" data-clear-style="">
            <svg viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path></svg>
          </div>
        </div>
        <div class="gjs-fields" data-sm-fields="" style="display: flex; align-items: center; gap: 4px;">
          <div class="gjs-field gjs-field-text" style="flex: 1;">
            <input type="text" placeholder="https://example.com" id="custom-url-input" style="width: 100%;">
          </div>
          <button
            id="custom-url-open-btn"
            title="Open URL in new tab"
            style="
              padding: 6px 10px;
              background: #444;
              border: 1px solid #555;
              border-radius: 3px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #ddd;
              font-size: 14px;
              line-height: 1;
              transition: background 0.2s;
            "
            onmouseover="this.style.background='#555'"
            onmouseout="this.style.background='#444'"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </button>
        </div>
      </div>
      <div class="gjs-sm-property gjs-sm-text gjs-sm-property__src gjs-sm-property--full" id="custom-image-src-field" style="display: none;">
        <div class="gjs-sm-label" data-sm-label="">
          <span class="gjs-sm-icon" title="">Image Source</span>
          <div class="gjs-sm-clear" style="display: none" data-clear-style="">
            <svg viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path></svg>
          </div>
        </div>
        <div class="gjs-fields" data-sm-fields="" style="display: flex; align-items: center; gap: 4px;">
          <div class="gjs-field gjs-field-text" style="flex: 1;">
            <input type="text" placeholder="https://example.com/image.jpg" id="custom-image-src-input" style="width: 100%;">
          </div>
          <button
            id="custom-image-src-open-btn"
            title="Open image in new tab"
            style="
              padding: 6px 10px;
              background: #444;
              border: 1px solid #555;
              border-radius: 3px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #ddd;
              font-size: 14px;
              line-height: 1;
              transition: background 0.2s;
            "
            onmouseover="this.style.background='#555'"
            onmouseout="this.style.background='#444'"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </button>
        </div>
      </div>
      <div class="gjs-sm-property gjs-sm-text gjs-sm-property__alt gjs-sm-property--full" id="custom-image-alt-field" style="display: none;">
        <div class="gjs-sm-label" data-sm-label="">
          <span class="gjs-sm-icon" title="">Alt Text</span>
        </div>
        <div class="gjs-fields" data-sm-fields="" style="display: flex; align-items: center; gap: 4px;">
          <div class="gjs-field gjs-field-text" style="flex: 1;">
            <input type="text" placeholder="Describe the image" id="custom-image-alt-input" style="width: 100%;">
          </div>
        </div>
      </div>
    `;

    // Insert at the beginning
    propertiesContainer.insertAdjacentHTML('afterbegin', urlFieldHTML);

    const urlField = document.getElementById('custom-url-field');
    const urlInput = document.getElementById('custom-url-input');
    const urlOpenBtn = document.getElementById('custom-url-open-btn');

    const imageSrcField = document.getElementById('custom-image-src-field');
    const imageSrcInput = document.getElementById('custom-image-src-input');
    const imageSrcOpenBtn = document.getElementById('custom-image-src-open-btn');

    const imageAltField = document.getElementById('custom-image-alt-field');
    const imageAltInput = document.getElementById('custom-image-alt-input');

    if (!urlField || !urlInput || !urlOpenBtn || !imageSrcField || !imageSrcInput || !imageSrcOpenBtn || !imageAltField || !imageAltInput) {
      console.error('Failed to create URL/Image Source/Alt fields');
      return;
    }

    // Handle URL input changes (for links)
    urlInput.addEventListener('input', (e) => {
      const component = editor.getSelected();
      if (!component) return;

      const newUrl = e.target.value;
      const tagName = component.get('tagName');
      const type = component.get('type');

      // If selected component is an MJML image, update its href attribute directly
      if (tagName === 'mj-image' || type === 'mj-image') {
        component.addAttributes({ href: newUrl });
        return;
      }

      // If selected component is a regular HTML image, check if parent is a link
      if (tagName === 'img' || type === 'image') {
        const parent = component.parent();
        if (parent) {
          const parentTag = parent.get('tagName');
          const parentType = parent.get('type');
          if (parentTag === 'a' || parentType === 'link') {
            // Update parent link's href
            parent.addAttributes({ href: newUrl });
            return;
          }
        }
      }

      // Otherwise, update the component's href directly
      component.addAttributes({ href: newUrl });
    });

    // Handle Image Source input changes
    // GrapesJS treats `src` as a model property (not just an HTML attribute).
    // Use component.set('src', value) which triggers the view's change:src listener
    // to automatically sync to HTML attributes. addAttributes({src}) gets overwritten.
    imageSrcInput.addEventListener('input', (e) => {
      const component = editor.getSelected();
      if (!component) return;

      const newSrc = e.target.value;
      const tagName = component.get('tagName');
      const type = component.get('type');

      // If selected component is an MJML image, update its src model property
      if (tagName === 'mj-image' || type === 'mj-image') {
        component.set('src', newSrc);
        return;
      }

      // If selected component is a regular HTML image, update its src model property
      if (tagName === 'img' || type === 'image') {
        component.set('src', newSrc);
        return;
      }

      // If it's a link, find the image inside and update its src
      const components = component.components();
      if (components && components.length > 0) {
        const imgComponent = components.models.find(c => {
          const cTag = c.get('tagName');
          const cType = c.get('type');
          return cTag === 'img' || cTag === 'mj-image' || cType === 'image' || cType === 'mj-image';
        });
        if (imgComponent) {
          imgComponent.set('src', newSrc);
        }
      }
    });

    // Handle URL open button click
    urlOpenBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const url = urlInput.value.trim();
      if (!url) {
        alert('Please enter a URL first');
        return;
      }

      // Ensure URL has protocol
      let fullUrl = url;
      if (!url.match(/^https?:\/\//i)) {
        fullUrl = 'https://' + url;
      }

      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    });

    // Handle Image Source open button click
    imageSrcOpenBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const url = imageSrcInput.value.trim();
      if (!url) {
        alert('Please enter an image URL first');
        return;
      }

      // Ensure URL has protocol
      let fullUrl = url;
      if (!url.match(/^https?:\/\//i)) {
        fullUrl = 'https://' + url;
      }

      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    });

    // Handle Alt Text input changes
    // alt is a regular HTML attribute, so addAttributes works fine
    imageAltInput.addEventListener('input', (e) => {
      const component = editor.getSelected();
      if (!component) return;

      const newAlt = e.target.value;
      const tagName = component.get('tagName');
      const type = component.get('type');

      // If selected component is an image, update its alt attribute
      if (tagName === 'mj-image' || type === 'mj-image' || tagName === 'img' || type === 'image') {
        component.addAttributes({ alt: newAlt });
        return;
      }

      // If it's a link, find the image inside and update its alt
      const components = component.components();
      if (components && components.length > 0) {
        const imgComponent = components.models.find(c => {
          const cTag = c.get('tagName');
          const cType = c.get('type');
          return cTag === 'img' || cTag === 'mj-image' || cType === 'image' || cType === 'mj-image';
        });
        if (imgComponent) {
          imgComponent.addAttributes({ alt: newAlt });
        }
      }
    });

    // Handle component selection
    editor.on('component:selected', (component) => {
      // Validate component exists and has required methods
      if (!component || typeof component.get !== 'function') {
        return;
      }

      try {
        const tagName = component.get('tagName');
        const type = component.get('type');
        const attributes = component.getAttributes();

        // Check if component is an image
        const isImage = tagName === 'img' || tagName === 'mj-image' || type === 'image' || type === 'mj-image';

        // Check if component is a link
        const isLink = tagName === 'a' || type === 'link';

        // Resolve the existing link on an image, if any (parent <a> for HTML
        // images, or the href attribute for MJML images), to prefill the field.
        let linkHref = '';

        if (isImage) {
          // For MJML images, check if component itself has href attribute
          if (tagName === 'mj-image' || type === 'mj-image') {
            linkHref = attributes.href || '';
          } else {
            // For regular HTML images, check if parent is a link
            const parent = component.parent();
            if (parent) {
              const parentTag = parent.get('tagName');
              const parentType = parent.get('type');
              if (parentTag === 'a' || parentType === 'link') {
                linkHref = parent.getAttributes().href || '';
              }
            }
          }
        }

        // Check if link contains an image
        let hasImageInside = false;
        let imageInsideSrc = '';
        if (isLink) {
          const componentEl = component.getEl();
          if (componentEl) {
            const imgElement = componentEl.querySelector('img, mj-image');
            if (imgElement) {
              hasImageInside = true;
              imageInsideSrc = imgElement.getAttribute('src') || imgElement.src || '';
            }
          }
        }

        // Determine which fields to show
        if (isImage) {
          // Any image, linked or not: ALWAYS show the URL field so a landing-page
          // link can be ADDED to an image that has none yet. Previously the
          // link-less branch hid the URL field, making it impossible to add a
          // first link to an image (customer-reported regression).
          urlField.style.display = '';
          urlInput.value = linkHref; // '' when the image has no link yet
          urlInput.placeholder = linkHref ? 'https://example.com' : 'Add link URL';

          imageSrcField.style.display = '';
          imageSrcInput.value = attributes.src || '';
          imageSrcInput.placeholder = attributes.src ? 'https://example.com/image.jpg' : 'Add image URL';

          imageAltField.style.display = '';
          imageAltInput.value = attributes.alt || '';
          imageAltInput.placeholder = 'Describe the image';
        } else if (isLink) {
          // Link: Always show URL field
          urlField.style.display = '';
          urlInput.value = attributes.href || '';
          urlInput.placeholder = attributes.href ? 'https://example.com' : 'Add link URL';

          // If link contains image, also show Image Source and Alt fields
          if (hasImageInside) {
            imageSrcField.style.display = '';
            imageSrcInput.value = imageInsideSrc;
            imageSrcInput.placeholder = imageInsideSrc ? 'https://example.com/image.jpg' : 'Add image URL';

            // Get alt from the image element inside the link
            const componentEl = component.getEl();
            const imgEl = componentEl ? componentEl.querySelector('img, mj-image') : null;
            const imageInsideAlt = imgEl ? (imgEl.getAttribute('alt') || '') : '';
            imageAltField.style.display = '';
            imageAltInput.value = imageInsideAlt;
            imageAltInput.placeholder = 'Describe the image';
          } else {
            imageSrcField.style.display = 'none';
            imageAltField.style.display = 'none';
          }
        } else {
          // Other elements: Show URL field if they can have links
          const canHaveUrl =
            tagName === 'button' ||
            type === 'text' ||
            component.get('editable') ||
            (component.getEl() && component.getEl().textContent && component.getEl().textContent.trim().length > 0);

          if (canHaveUrl) {
            urlField.style.display = '';
            urlInput.value = attributes.href || '';
            urlInput.placeholder = attributes.href ? 'https://example.com' : 'Add URL (e.g., https://example.com)';
          } else {
            urlField.style.display = 'none';
          }

          // Hide Image Source and Alt fields for non-image components
          imageSrcField.style.display = 'none';
          imageAltField.style.display = 'none';
        }
      } catch (error) {
        // Silently handle errors to avoid breaking panel buttons
        console.error('Error in component selection handler:', error);
      }
    });

    // Handle component deselection
    editor.on('component:deselected', () => {
      urlField.style.display = 'none';
      imageSrcField.style.display = 'none';
      imageAltField.style.display = 'none';
    });
  }

  /**
   * Initialize UX enhancements
   */
  initUXEnhancements() {
    if (!this.editor) {
      throw Error('No editor found');
    }

    // Initialize UX interaction enhancements
    initGrapesJSUXEnhancements(this.editor);

    // Initialize enhanced UX improvements (minimal safe version - scrollbars only)
    initEnhancedUXImprovements(this.editor);
  }

  /**
   * Disable CKEditor modal and force inline editing
   */
  disableCKEditorModal() {
    if (!this.editor) {
      return;
    }

    // Wait for editor to fully load
    this.editor.on('load', () => {
      const commands = this.editor.Commands;

      // Try to find and override the CKEditor modal command
      const modalCommands = ['ck-editor', 'ckeditor', 'open-ckeditor'];

      modalCommands.forEach((cmdId) => {
        const cmd = commands.get(cmdId);
        if (cmd) {
          // Override the run method to prevent modal
          commands.add(cmdId, {
            run: (editor, sender, opts = {}) => {
              const component = editor.getSelected();
              if (component) {
                // Enable RTE directly instead of opening modal
                editor.RichTextEditor.enable(component);
              }
            },
            stop: (editor) => {
              editor.RichTextEditor.disable();
            },
          });
        }
      });

      // Also override the default text edit behavior
      this.editor.on('component:mount', (component) => {
        if (component.get('type') === 'text' || component.get('editable')) {
          // Override the component's default behavior
          component.set('editable', true);
        }
      });
    });
  }

  /**
   * Enable inline text editing - edit directly in canvas without modal
   * Using direct contenteditable approach instead of GrapesJS RTE
   */
  enableInlineEditing() {
    if (!this.editor) {
      return;
    }

    let currentlyEditing = null;

    // Listen for component mounting to attach double-click handlers
    this.editor.on('component:mount', (component) => {
      const type = component.get('type');
      const tagName = component.get('tagName');

      const isEditable = type === 'text' ||
                        type === 'link' ||
                        tagName === 'a' ||
                        this.hasEditableContent(component);

      if (isEditable) {
        const el = component.getEl();
        if (el && !el.__editableInitialized) {
          el.__editableInitialized = true;

          el.addEventListener('dblclick', (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Disable previous if exists (let blur handler take care of it)
            if (currentlyEditing && currentlyEditing.el && currentlyEditing.el !== el) {
              currentlyEditing.el.contentEditable = false;
              currentlyEditing.el.blur();
            }

            // Store original content before editing
            const originalContent = el.innerHTML;

            // Fix text color inheritance for inline editing.
            // MJML components (mj-text) often set color:#ffffff on the wrapper <div>
            // while child elements (<a>, <span>) override it. When typing at the
            // boundary of a child element, new text inherits the wrapper's white
            // color, making it invisible. Fix by temporarily setting the editable
            // element's color to match its visible text content.
            this.fixEditableTextColor(el);

            // Enable editing on this element
            el.contentEditable = true;
            el.focus();

            // For inline elements like <a>, set up a handler that keeps typed
            // characters inside the element. When both the <a> and its parent
            // <div> are contenteditable, the browser may place new characters
            // outside the <a> as sibling text nodes. This handler detects
            // orphaned text nodes and merges them back into the <a>.
            this.setupInlineEditGuard(el);

            // Store editing state on element itself to avoid race conditions
            el.__shouldSaveOnBlur = true;
            el.__originalContent = originalContent;

            currentlyEditing = { el, component };

            // Place cursor at end
            setTimeout(() => {
              try {
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(el);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
              } catch (err) {
                // cursor restore failed — non-critical
              }
            }, 0);
          });

          // Blur to save or restore
          el.addEventListener('blur', () => {
            if (el.contentEditable === 'true') {
              el.contentEditable = false;

              // Restore original text color on the container if we changed it
              this.restoreEditableTextColor(el);

              // Remove the inline edit guard
              this.removeInlineEditGuard(el);

              // Check element's own save flag
              if (el.__shouldSaveOnBlur) {
                // Save content back to component
                component.set('content', el.innerHTML);
              } else {
                // Restore original content if ESC was pressed
                if (el.__originalContent !== undefined) {
                  el.innerHTML = el.__originalContent;
                }
              }

              // Clean up
              el.__shouldSaveOnBlur = true; // Reset for next edit
              delete el.__originalContent;

              if (currentlyEditing && currentlyEditing.el === el) {
                currentlyEditing = null;
              }
            }
          });

          // ESC key to cancel and restore original content
          el.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
              // Set this element's flag to NOT save on blur
              el.__shouldSaveOnBlur = false;
              el.blur();
            }
          });
        }
      }
    });

    // Auto-expand Typography section when selecting text
    this.editor.on('component:selected', (component) => {
      // Validate component exists and has required methods
      if (!component || typeof component.get !== 'function') {
        return;
      }

      try {
        const type = component.get('type');
        const tagName = component.get('tagName');

        const isEditable = type === 'text' ||
                          type === 'link' ||
                          tagName === 'a' ||
                          component.get('editable');

        if (isEditable) {
          this.expandTypographySection();
        }

        // Show/hide URL property based on component type
        this.updateUrlPropertyVisibility(component);
      } catch (error) {
        // Silently handle errors to avoid breaking panel buttons
      }
    });

    // Click outside to disable editing
    this.editor.on('load', () => {
      const canvas = this.editor.Canvas.getFrameEl();
      if (canvas && canvas.contentDocument) {
        canvas.contentDocument.addEventListener('click', (e) => {
          if (currentlyEditing && !currentlyEditing.el.contains(e.target)) {
            currentlyEditing.el.blur();
          }
        });
      }
    });
  }

  /**
   * Fix text color on a contenteditable element to prevent invisible text.
   *
   * MJML components like mj-text often set color:#ffffff on the wrapper <div>
   * while child elements (<a>, <span>) override it with a visible color.
   * When typing at the boundary of a child element, new characters inherit
   * the wrapper's white color and become invisible on a white background.
   *
   * This method detects the mismatch and temporarily sets the editable
   * element's (and its parent's) color to match the first visible child's
   * text color so newly typed characters are visible.
   */
  fixEditableTextColor(el) {
    try {
      const doc = el.ownerDocument;
      const win = doc.defaultView;
      if (!win) return;

      // Walk up the element and its parent to check for light-on-light text
      const targets = [el, el.parentElement].filter(Boolean);

      for (const target of targets) {
        const style = win.getComputedStyle(target);
        const color = style.color;
        const bgColor = style.backgroundColor;

        // Parse the text color to check if it's very light (near white)
        if (!this.isLightColor(color)) continue;

        // Find a child element with a darker (visible) text color
        const childColor = this.findVisibleChildTextColor(target, win);
        if (childColor && !this.isLightColor(childColor)) {
          // Save the original color and apply the fix
          target.__originalColor = target.style.color;
          target.style.color = childColor;
        }
      }
    } catch (e) {
      // Don't break editing if color fix fails
    }
  }

  /**
   * Restore the original text color after editing ends.
   */
  restoreEditableTextColor(el) {
    try {
      const targets = [el, el.parentElement].filter(Boolean);
      for (const target of targets) {
        if (target.__originalColor !== undefined) {
          target.style.color = target.__originalColor;
          delete target.__originalColor;
        }
      }
    } catch (e) {}
  }

  /**
   * Check if a CSS color value is very light (near white).
   * Returns true for colors like rgb(255,255,255), #ffffff, white, etc.
   */
  isLightColor(colorStr) {
    if (!colorStr) return false;
    const m = colorStr.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return false;
    const r = parseInt(m[1]);
    const g = parseInt(m[2]);
    const b = parseInt(m[3]);
    // Luminance threshold: if all channels are above 200, it's very light
    return r > 200 && g > 200 && b > 200;
  }

  /**
   * Find the computed text color of the first child element that has
   * a visible (non-light) text color.
   */
  findVisibleChildTextColor(el, win) {
    const children = el.querySelectorAll('*');
    for (const child of children) {
      if (child.textContent && child.textContent.trim()) {
        const childStyle = win.getComputedStyle(child);
        if (childStyle.color && !this.isLightColor(childStyle.color)) {
          return childStyle.color;
        }
      }
    }
    return null;
  }

  /**
   * Set up an input handler that keeps typed characters inside an inline
   * element (like <a>) during contenteditable editing.
   *
   * When both an <a> and its parent <div> are contenteditable, the browser
   * may place new characters outside the <a> as sibling text nodes in the
   * parent. This handler detects those orphaned text nodes after each input
   * event and moves them back into the <a>.
   */
  setupInlineEditGuard(el) {
    const parent = el.parentElement;
    if (!parent) return;

    // Only needed for inline elements inside a contenteditable parent
    const tag = el.tagName ? el.tagName.toLowerCase() : '';
    const isInline = tag === 'a' || tag === 'span' || tag === 'strong' ||
                     tag === 'em' || tag === 'b' || tag === 'i' || tag === 'u';
    if (!isInline) return;

    const handler = () => {
      // Find text nodes that are direct children of parent (outside our element)
      const nodesToMove = [];
      for (let i = 0; i < parent.childNodes.length; i++) {
        const child = parent.childNodes[i];
        if (child === el) continue;
        if (child.nodeType === 3 /* TEXT_NODE */ && child.textContent) {
          nodesToMove.push(child);
        }
      }

      if (nodesToMove.length === 0) return;

      // Preserve cursor position
      const doc = el.ownerDocument;
      const win = doc.defaultView;
      const sel = win.getSelection();
      let savedOffset = -1;
      let cursorAtEnd = false;

      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        // Check if cursor is in one of the nodes we're about to move
        for (const node of nodesToMove) {
          if (range.startContainer === node) {
            savedOffset = range.startOffset;
            // Check if node is after the element
            cursorAtEnd = !!(el.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING);
            break;
          }
        }
      }

      // Move orphaned text nodes into the element
      for (const node of nodesToMove) {
        if (el.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) {
          // node is after el → append to end
          el.appendChild(node);
        } else {
          // node is before el → prepend
          el.insertBefore(node, el.firstChild);
        }
      }

      // Restore cursor position
      if (savedOffset >= 0 && sel) {
        try {
          const range = doc.createRange();
          // Find the moved text node (now inside el)
          const textNode = cursorAtEnd ? el.lastChild : el.firstChild;
          if (textNode && textNode.nodeType === 3) {
            const offset = Math.min(savedOffset, textNode.textContent.length);
            range.setStart(textNode, offset);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        } catch (e) {}
      }
    };

    parent.addEventListener('input', handler);
    el.__inlineEditGuardHandler = handler;
    el.__inlineEditGuardParent = parent;
  }

  /**
   * Remove the inline edit guard when editing ends.
   */
  removeInlineEditGuard(el) {
    if (el.__inlineEditGuardHandler && el.__inlineEditGuardParent) {
      el.__inlineEditGuardParent.removeEventListener('input', el.__inlineEditGuardHandler);
      delete el.__inlineEditGuardHandler;
      delete el.__inlineEditGuardParent;
    }
  }


  /**
   * Expand Typography section in Style Manager
   */
  expandTypographySection() {
    const styleManager = this.editor.StyleManager;
    const typographySector = styleManager.getSector('typography');

    if (typographySector && !typographySector.get('open')) {
      typographySector.set('open', true);
    }
  }

  /**
   * Update URL property visibility based on selected component
   */
  updateUrlPropertyVisibility(component) {
    const styleManager = this.editor.StyleManager;
    const typographySector = styleManager.getSector('typography');

    if (!typographySector) {
      return;
    }

    // Find the URL property
    const properties = typographySector.get('properties');
    const urlProperty = properties.find(prop => prop.get('property') === 'href-link');

    if (!urlProperty) {
      return;
    }

    // Check if component is a link or has href attribute
    const tagName = component.get('tagName');
    const type = component.get('type');
    const hasHref = component.getAttributes().href !== undefined;
    const isLink = tagName === 'a' || type === 'link' || hasHref;

    // Show or hide the property
    const urlPropertyView = styleManager.getProperty('typography', 'href-link');
    if (urlPropertyView && urlPropertyView.view) {
      const el = urlPropertyView.view.el;
      if (el) {
        el.style.display = isLink ? 'block' : 'none';
      }
    }
  }

  /**
   * Check if component has editable text content
   */
  hasEditableContent(component) {
    const el = component.getEl();
    if (!el) return false;

    // Check if element has direct text content
    const hasText = el.textContent && el.textContent.trim().length > 0;

    // Check if it's a link or text-containing element
    const tagName = el.tagName ? el.tagName.toLowerCase() : '';
    const editableTags = ['a', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div', 'td', 'th', 'li'];

    return hasText && editableTags.includes(tagName);
  }

  /**
   * Initialize theme manager for dark/light mode support
   */
  initTheme() {
    if (!this.theme) {
      this.theme = new GrapesJSTheme();
      this.theme.init();
    }
  }

  /**
   * Initialize block icons for better visual identification
   */
  initBlockIcons() {
    BlockIcons.init(this.editor);
  }

  /**
   * Initialize canvas improvements (grid, snap, responsive)
   */
  initCanvasImprovements() {
    if (!this.canvasImprovements) {
      this.canvasImprovements = new CanvasImprovements(this.editor);
      // Make it available globally for keyboard shortcuts
      window.canvasImprovements = this.canvasImprovements;
    }
  }

  /**
   * Initialize keyboard shortcuts
   */
  initKeyboardShortcuts() {
    if (!this.keyboardShortcuts) {
      this.keyboardShortcuts = new KeyboardShortcuts(this.editor);
    }
  }

  static getMauticConf(mode) {
    return {
      mode,
    };
  }

  /**
   * Get Style Manager configuration with Typography, Dimension, and Decorations
   */
  static getStyleManagerConf() {
    return {
      sectors: [
        {
          name: 'Typography',
          id: 'typography',
          open: false,
          buildProps: [
            'font-family',
            'font-size',
            'font-weight',
            'letter-spacing',
            'line-height',
            'color',
            'text-align',
            'text-decoration',
            'text-transform',
          ],
          properties: [
            {
              name: 'Font Family',
              property: 'font-family',
              type: 'select',
              defaults: 'Arial, Helvetica, sans-serif',
              options: [
                { value: 'Arial, Helvetica, sans-serif', name: 'Arial' },
                { value: 'Georgia, serif', name: 'Georgia' },
                { value: 'Times New Roman, serif', name: 'Times New Roman' },
                { value: 'Courier New, monospace', name: 'Courier New' },
                { value: 'Verdana, sans-serif', name: 'Verdana' },
                { value: 'Trebuchet MS, sans-serif', name: 'Trebuchet MS' },
              ],
            },
            {
              name: 'Font Size',
              property: 'font-size',
              type: 'select',
              defaults: '14px',
              options: [
                { value: '10px', name: '10px' },
                { value: '12px', name: '12px' },
                { value: '14px', name: '14px' },
                { value: '16px', name: '16px' },
                { value: '18px', name: '18px' },
                { value: '20px', name: '20px' },
                { value: '24px', name: '24px' },
                { value: '28px', name: '28px' },
                { value: '32px', name: '32px' },
                { value: '36px', name: '36px' },
              ],
            },
            {
              name: 'Font Weight',
              property: 'font-weight',
              type: 'select',
              defaults: '400',
              options: [
                { value: '300', name: 'Light' },
                { value: '400', name: 'Normal' },
                { value: '500', name: 'Medium' },
                { value: '600', name: 'Semi-bold' },
                { value: '700', name: 'Bold' },
              ],
            },
            {
              name: 'Text Color',
              property: 'color',
              type: 'color',
            },
            {
              name: 'Text Align',
              property: 'text-align',
              type: 'radio',
              defaults: 'left',
              options: [
                { value: 'left', name: 'Left', className: 'fa fa-align-left' },
                { value: 'center', name: 'Center', className: 'fa fa-align-center' },
                { value: 'right', name: 'Right', className: 'fa fa-align-right' },
                { value: 'justify', name: 'Justify', className: 'fa fa-align-justify' },
              ],
            },
          ],
        },
        {
          name: 'Dimension',
          id: 'dimension',
          open: false,
          buildProps: [
            'width',
            'height',
            'max-width',
            'min-height',
            'margin',
            'padding',
          ],
        },
        {
          name: 'Decorations',
          id: 'decorations',
          open: false,
          buildProps: [
            'background-color',
            'background',
            'border',
            'border-radius',
            'box-shadow',
          ],
        },
        {
          name: 'Extra',
          id: 'extra',
          open: false,
          buildProps: [
            'opacity',
            'transition',
            'transform',
          ],
        },
      ],
    };
  }

  /**
   * Placeholder for Layer Manager initialization
   * Note: Layer Manager button is hidden via CSS (grapesjs-custom.css)
   * because it doesn't work properly with the MJML plugin
   * @param {Object} editor - GrapesJS editor instance
   */
  static initializeLayerManager(editor) {
    // Layer Manager button is hidden via CSS rule in grapesjs-custom.css:
    // .gjs-pn-btn[title*="Layer" i] { display: none !important; }
    // This is because the LayerManager doesn't work with MJML plugin
  }

  /**
   * Initialize the builder in the landingapge mode
   */
  initPage() {
    // Launch GrapesJS with body part
    this.editor = grapesjs.init({
      clearOnRender: true,
      container: '.builder-panel',
      components: contentService.getOriginalContentHtml().body.innerHTML,
      height: '100%',
      canvas: {
        styles: contentService.getStyles(),
      },
      storageManager: false, // https://grapesjs.com/docs/modules/Storage.html#basic-configuration
      assetManager: this.getAssetManagerConf(),
      styleManager: BuilderService.getStyleManagerConf(),
      layerManager: {
        appendTo: '.gjs-pn-views-container',
      },
      // CKEditor plugin removed - using native GrapesJS inline editing
      plugins: [grapesjswebpage, grapesjspostcss, grapesjsmautic],
      pluginsOpts: {
        [grapesjswebpage]: {
          formsOpts: false,
        },
        grapesjsmautic: BuilderService.getMauticConf('page-html'),
      },
    });

    // Fix Layer Manager: Ensure it's properly initialized and rendered
    BuilderService.initializeLayerManager(this.editor);

    return this.editor;
  }

  initEmailMjml() {
    const components = mjmlService.getOriginalContentMjml();
    // validate
    mjmlService.mjmlToHtml(components);

    const styles = [
      `${mauticBaseUrl}plugins/GrapesJsBuilderBundle/Assets/library/js/grapesjs-editor.css`
    ];

    this.editor = grapesjs.init({
      clearOnRender: true,
      container: '.builder-panel',
      components,
      height: '100%',
      canvas: {
        styles,
      },
      storageManager: false,
      assetManager: this.getAssetManagerConf(),
      styleManager: BuilderService.getStyleManagerConf(),
      layerManager: {
        appendTo: '.gjs-pn-views-container',
      },
      // CKEditor plugin removed - using native GrapesJS inline editing
      plugins: [grapesjsmjml, grapesjspostcss, grapesjsmautic],
      pluginsOpts: {
        grapesjsmjml: {
          // Ensure panels aren't hidden/disabled
          resetBlocks: false,
        },
        grapesjsmautic: BuilderService.getMauticConf('email-mjml'),
      },
    });

    this.editor.BlockManager.get('mj-button').set({
      content: '<mj-button href="https://">Button</mj-button>',
    });

    // Add mobile-safe multi-image "Image Row" section blocks and surface the
    // existing mj-group blocks under the Sections category (see method docs).
    BuilderService.addMjmlImageRowBlocks(this.editor);

    // Guarantee dropped images serialize as valid <mj-image> so they survive
    // the server-side MJML compile used by Preview/send (see method docs).
    BuilderService.addMjmlImageSafety(this.editor);

    // Self-serve mobile-responsive controls (keep-side-by-side / reverse / show-on)
    // as native traits, so marketers never need Code Mode for these (see method docs).
    BuilderService.addResponsiveMobileTraits(this.editor);

    // Brand Kit (global fonts/colors → mj-head), Saved Blocks (reusable sections),
    // and Smart Check (in-builder accessibility/deliverability linter).
    BuilderService.addBrandKit(this.editor);
    BuilderService.addSavedBlocks(this.editor);
    BuilderService.addSmartCheck(this.editor);

    // AI "Rewrite" action on text blocks (Claude via the email AI endpoint).
    // No-op unless the instance has AI configured (window.mauticEmailAiEnabled).
    BuilderService.addAiRewrite(this.editor);

    // Preview text (inbox preheader) editor — always available; AI-generate when
    // configured. Writes <mj-preview> into <mj-head> (MJML mode).
    BuilderService.addPreheader(this.editor);

    // Fix Layer Manager: Ensure it's properly initialized and rendered
    BuilderService.initializeLayerManager(this.editor);

    return this.editor;
  }

  /**
   * Register mobile-safe multi-image "Image Row" Section blocks.
   *
   * The stock Section blocks (One/Two/Three Columns) emit bare
   * `<mj-section><mj-column>…` which, by MJML's default responsive behaviour,
   * STACK vertically on mobile. Marketers who want a header of 2–3 small images
   * side-by-side had no way to keep them side-by-side on mobile — MJML's
   * `mj-group` does exactly that, but there was no block or trait exposing it.
   *
   * These two blocks wrap the columns in `<mj-group>` with PERCENT widths
   * (mj-group requires % widths, not px) so the images stay in one row on
   * mobile. Each column holds a real `<mj-image>` (valid MJML → renders in
   * Preview, and linkable via the image URL field). We also move the existing
   * `grid-items` / `list-items` mj-group blocks into the Sections category so
   * the mj-group pattern is discoverable.
   */
  static addMjmlImageRowBlocks(editor) {
    if (!editor) return;

    const bm = editor.BlockManager;
    const sectionCategory = Mautic.translate('grapesjsbuilder.categorySectionLabel');
    // Self-contained SVG data-URI placeholder. The previous `via.placeholder.com`
    // URL was doubly broken — that host was shut down (every placeholder 404'd on
    // canvas and in the compiled email) and the `#7f7f7f` made the colour a URL
    // fragment. A data URI can never 404 and needs no external service.
    const placeholder = (n) => {
      const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="172" height="215">` +
        `<rect width="100%" height="100%" fill="#7f7f7f"/>` +
        `<text x="50%" y="50%" fill="#ffffff" font-family="Arial,Helvetica,sans-serif" ` +
        `font-size="15" text-anchor="middle" dominant-baseline="middle">Image ${n}</text></svg>`;
      return `data:image/svg+xml,${encodeURIComponent(svg)}`;
    };

    const columns = (widths) =>
      widths
        .map(
          (w, i) =>
            `<mj-column width="${w}"><mj-image height="auto" src="${placeholder(i + 1)}"></mj-image></mj-column>`
        )
        .join('');

    bm.add('mj-image-row-2', {
      label: 'Image Row (2)',
      category: sectionCategory,
      attributes: { class: 'fa fa-images' },
      content: `<mj-section><mj-group>${columns(['50%', '50%'])}</mj-group></mj-section>`,
    });

    bm.add('mj-image-row-3', {
      label: 'Image Row (3)',
      category: sectionCategory,
      attributes: { class: 'fa fa-images' },
      content: `<mj-section><mj-group>${columns(['33.33%', '33.33%', '33.34%'])}</mj-group></mj-section>`,
    });

    // Surface the existing mj-group example blocks under Sections so the
    // side-by-side-on-mobile pattern is discoverable next to the column blocks,
    // and repair their dead `via.placeholder.com` image URLs (same defect).
    ['grid-items', 'list-items'].forEach((id) => {
      const block = bm.get(id);
      if (!block) return;
      block.set('category', sectionCategory);
      const content = block.get('content');
      if (typeof content === 'string' && content.includes('via.placeholder')) {
        let i = 0;
        block.set(
          'content',
          content.replace(/https?:\/\/via\.placeholder\.com\/[^"'\s)]+/g, () => {
            i += 1;
            return placeholder(i);
          })
        );
      }
    });
  }

  /**
   * Guarantee that images end up as valid `<mj-image>` in the exported MJML.
   *
   * The canvas renders a live `<img>` DOM node, but Preview/send RE-SERIALISE
   * the component tree to an MJML string and recompile it (server-side, a
   * stricter MJML compiler than the editor's). A raw core `image`/`<img>`
   * component dropped into an `mj-column` (e.g. via image-file drop or the
   * asset manager) is NOT a valid mj-column child, so the compiler silently
   * drops it — the image shows on the canvas but VANISHES in the rendered
   * email. Convert any such raw image into a proper `<mj-image>`, carrying its
   * src on the MODEL (mj-image extends core image; attribute-only src gets
   * overwritten by the view's change:src sync) so the exporter emits
   * `<mj-image src="…">`.
   *
   * Registered AFTER grapesjs.init(). NOTE: component:add ALSO fires while the
   * editor parses already-loaded email content, so this handler must be careful
   * NOT to rewrite hand-authored markup — see the <mj-raw> guard below.
   */
  static addMjmlImageSafety(editor) {
    if (!editor) return;

    const isRawImage = (c) => {
      if (!c) return false;
      const type = c.get('type');
      const tag = c.get('tagName');
      const isImg = type === 'image' || tag === 'img';
      const isMjImage = type === 'mj-image' || tag === 'mj-image';
      return isImg && !isMjImage;
    };

    // Images living inside an MJML element whose CONTENT is raw HTML (mj-raw,
    // mj-text, mj-button, mj-navbar-link, mj-social-element, mj-accordion-*) are
    // hand-authored markup that must be preserved verbatim — e.g. the weekly
    // "Flash Deal" product grid (sliced <td> <img> width:100%, inside mj-raw) and
    // the inline "Free Shipping" truck icon (width="24", inside mj-text). Coercing
    // them to <mj-image> keeps only src/alt/href and DROPS the inline style/width,
    // so images render at natural size and break the layout. These containers are
    // valid MJML as-is and never need the mj-column drop safety net — skip them.
    // (The net still fires for genuine bare images dropped into an mj-column.)
    const RAW_HTML_CONTAINERS = [
      'mj-raw', 'mj-text', 'mj-button', 'mj-navbar-link',
      'mj-social-element', 'mj-accordion-text', 'mj-accordion-title',
    ];
    const isInsideRawHtml = (c) => {
      let p = c && c.parent && c.parent();
      while (p) {
        const type = p.get('type');
        const tag = p.get('tagName');
        if (RAW_HTML_CONTAINERS.includes(type) || RAW_HTML_CONTAINERS.includes(tag)) return true;
        p = p.parent && p.parent();
      }
      return false;
    };

    editor.on('component:add', (component) => {
      try {
        if (!isRawImage(component)) return;
        if (isInsideRawHtml(component)) return;

        const attrs = component.getAttributes() || {};
        const src = component.get('src') || attrs.src || '';
        const newAttrs = {};
        if (attrs.alt) newAttrs.alt = attrs.alt;
        if (attrs.href) newAttrs.href = attrs.href;

        const wasSelected = editor.getSelected() === component;

        const replacement = component.replaceWith({
          type: 'mj-image',
          attributes: newAttrs,
        });
        const mjImage = Array.isArray(replacement) ? replacement[0] : replacement;
        if (!mjImage) return;

        // src is a model property on mj-image — set it on the model so export
        // emits it (addAttributes({src}) would be overwritten by the view sync).
        if (src) mjImage.set('src', src);
        if (wasSelected) editor.select(mjImage);
      } catch (error) {
        // Never let image coercion break the editor.
        console.error('MJML image safety conversion failed:', error);
      }
    });
  }

  /**
   * Register self-serve mobile-responsive controls as native GrapesJS traits on
   * MJML components, so a marketer can control mobile behaviour WITHOUT dropping
   * to Code Mode (previously the only way — e.g. hand-wrapping columns in an
   * `<mj-group>` at percentage widths just to keep a row of images side-by-side
   * on phones, the exact task that forced a developer round-trip).
   *
   * Three controls, each emitting MJML whose COMPILED output was verified:
   *  - "Keep side-by-side on mobile" (mj-section): wraps the section's columns in
   *    an `<mj-group>` and equalises their widths to percentages (mj-group needs
   *    % widths, never px — the footgun behind the original bug). Off → unwraps
   *    so the columns stack again.
   *  - "Reverse order on mobile" (mj-section): sets `direction="rtl"`, so stacked
   *    columns reverse (the last becomes first on a phone).
   *  - "Show on" Both / Desktop / Mobile (section/column/image/text/button/hero):
   *    toggles a css-class and injects one media-query block into `<mj-head>`.
   *
   * Traits are added on selection (idempotently) and surface in the builder's
   * Settings panel like every other native trait.
   */
  static addResponsiveMobileTraits(editor) {
    if (!editor) return;

    const label = (key, fallback) => {
      const s = Mautic.translate(key);
      return !s || s === key ? fallback : s;
    };
    const typeIs = (comp, name) => comp.get('type') === name || comp.get('tagName') === name;
    const childModels = (comp) => (comp && comp.components() ? comp.components().models : []);

    // ---- keep-side-by-side (mj-group) --------------------------------------
    const isColumn = (c) => typeIs(c, 'mj-column');
    const isGroup = (c) => typeIs(c, 'mj-group');
    const findGroup = (section) => childModels(section).find(isGroup);
    const isKeptInline = (section) => childModels(section).some(isGroup);

    // Equal percentages summing to ~100; the last column absorbs the rounding.
    const equalPercents = (n) => {
      const base = Math.floor(10000 / n) / 100; // 2dp, e.g. 33.33
      const widths = new Array(n).fill(base);
      widths[n - 1] = Math.round((100 - base * (n - 1)) * 100) / 100;
      return widths.map((w) => `${w}%`);
    };

    // Serialize a column to MJML, guaranteeing an explicit % width in the tag.
    const columnMjmlWithWidth = (col, pct) => {
      col.addAttributes({ width: pct });
      const mjml = col.toHTML();
      const openTag = mjml.slice(0, mjml.indexOf('>') + 1);
      if (/\bwidth\s*=/.test(openTag)) return mjml;
      return mjml.replace(/^<mj-column/, `<mj-column width="${pct}"`);
    };

    const setKeepInline = (section, on) => {
      if (!section) return;
      if (on) {
        if (findGroup(section)) return; // already grouped
        const cols = childModels(section).filter(isColumn);
        if (cols.length < 2) return; // nothing to keep side-by-side
        const pcts = equalPercents(cols.length);
        const inner = cols.map((c, i) => columnMjmlWithWidth(c, pcts[i])).join('');
        section.components(`<mj-group>${inner}</mj-group>`);
      } else {
        const group = findGroup(section);
        if (!group) return;
        const inner = childModels(group).filter(isColumn).map((c) => c.toHTML()).join('');
        section.components(inner);
      }
    };

    // ---- show-on (hide-on-mobile / hide-on-desktop via css-class) -----------
    const HIDE_MOBILE = 'mj-hide-mobile';
    const HIDE_DESKTOP = 'mj-hide-desktop';
    const STYLE_MARKER = 'mautic-responsive-hide';
    const HIDE_CSS_BODY =
      `.${HIDE_MOBILE}{}` +
      `@media (max-width:480px){.${HIDE_MOBILE}{display:none!important;max-height:0!important;overflow:hidden!important;mso-hide:all;}}` +
      `.${HIDE_DESKTOP}{display:none;max-height:0;overflow:hidden;mso-hide:all;}` +
      `@media (max-width:480px){.${HIDE_DESKTOP}{display:block!important;max-height:none!important;overflow:visible!important;}}`;

    const getCssClasses = (comp) => {
      const cls = (comp.getAttributes()['css-class'] || '').trim();
      return cls ? cls.split(/\s+/) : [];
    };
    const setCssClasses = (comp, list) => {
      const attrs = { ...comp.getAttributes() };
      const val = list.join(' ').trim();
      if (val) attrs['css-class'] = val;
      else delete attrs['css-class'];
      comp.setAttributes(attrs);
    };
    const showOnValue = (comp) => {
      const cls = getCssClasses(comp);
      if (cls.includes(HIDE_MOBILE)) return 'desktop';
      if (cls.includes(HIDE_DESKTOP)) return 'mobile';
      return '';
    };

    // Inject a marked <mj-style> into <mj-head> exactly once per marker id, so
    // generated responsive CSS (hide rules, mobile font sizes) rides along with
    // the email and survives the server-side compile. Reused by several controls.
    const ensureHeadStyle = (markerId, css) => {
      const root = editor.getWrapper();
      if (!root) return;
      let head = null;
      const findHead = (comp) => {
        if (!comp || head) return;
        if (typeIs(comp, 'mj-head')) { head = comp; return; }
        childModels(comp).forEach(findHead);
      };
      findHead(root);
      if (!head) {
        const mjmlRoot = typeIs(root, 'mjml')
          ? root
          : childModels(root).find((c) => typeIs(c, 'mjml')) || root;
        const added = mjmlRoot.append('<mj-head></mj-head>', { at: 0 });
        head = Array.isArray(added) ? added[0] : added;
      }
      if (!head) return;
      const marker = `/*${markerId}*/`;
      const present = childModels(head).some((c) => (c.toHTML() || '').includes(marker));
      if (!present) head.append(`<mj-style>${marker}${css}</mj-style>`);
    };
    const ensureHideStyles = () => ensureHeadStyle(STYLE_MARKER, HIDE_CSS_BODY);

    const setShowOn = (comp, value) => {
      const cls = getCssClasses(comp).filter((c) => c !== HIDE_MOBILE && c !== HIDE_DESKTOP);
      if (value === 'desktop') cls.push(HIDE_MOBILE);
      if (value === 'mobile') cls.push(HIDE_DESKTOP);
      setCssClasses(comp, cls);
      if (value) ensureHideStyles();
    };

    // ---- mobile-only font size (per-device override) -----------------------
    // Encoded in a shared css-class `mmobfs-<px>` so components wanting the same
    // mobile size share one head rule. Descendant selectors reach the text nodes
    // that MJML renders (div/p for mj-text, a for mj-button).
    const MOB_FS_PREFIX = 'mmobfs-';
    const mobileFsValue = (comp) => {
      const cls = getCssClasses(comp).find((c) => c.startsWith(MOB_FS_PREFIX));
      return cls ? cls.slice(MOB_FS_PREFIX.length) : '';
    };
    const setMobileFs = (comp, px) => {
      const n = parseInt(px, 10);
      const cls = getCssClasses(comp).filter((c) => !c.startsWith(MOB_FS_PREFIX));
      if (n && n > 0) {
        cls.push(`${MOB_FS_PREFIX}${n}`);
        setCssClasses(comp, cls);
        ensureHeadStyle(
          `mautic-mobile-fs-${n}`,
          `@media (max-width:480px){` +
            `.${MOB_FS_PREFIX}${n},.${MOB_FS_PREFIX}${n} div,.${MOB_FS_PREFIX}${n} p,` +
            `.${MOB_FS_PREFIX}${n} a,.${MOB_FS_PREFIX}${n} span` +
            `{font-size:${n}px!important;line-height:normal!important;}}`
        );
      } else {
        setCssClasses(comp, cls);
      }
    };

    // ---- custom trait input types ------------------------------------------
    const tm = editor.TraitManager;

    tm.addType('mautic-keep-inline', {
      createInput() {
        const el = document.createElement('input');
        el.type = 'checkbox';
        el.style.cssText = 'width:18px;height:18px;cursor:pointer;';
        return el;
      },
      onEvent({ elInput, component }) {
        setKeepInline(component, elInput.checked);
        editor.select(component); // keep selection stable after the restructure
      },
      onUpdate({ elInput, component }) {
        elInput.checked = isKeptInline(component);
      },
    });

    tm.addType('mautic-show-on', {
      createInput() {
        const el = document.createElement('select');
        el.style.cssText = 'width:100%;';
        [
          ['', label('grapesjsbuilder.showOnBoth', 'Desktop + Mobile')],
          ['desktop', label('grapesjsbuilder.showOnDesktop', 'Desktop only')],
          ['mobile', label('grapesjsbuilder.showOnMobile', 'Mobile only')],
        ].forEach(([value, text]) => {
          const o = document.createElement('option');
          o.value = value;
          o.textContent = text;
          el.appendChild(o);
        });
        return el;
      },
      onEvent({ elInput, component }) { setShowOn(component, elInput.value); },
      onUpdate({ elInput, component }) { elInput.value = showOnValue(component); },
    });

    tm.addType('mautic-mobile-fs', {
      createInput() {
        const el = document.createElement('input');
        el.type = 'number';
        el.min = '8';
        el.max = '100';
        el.placeholder = label('grapesjsbuilder.mobileFontSizePlaceholder', 'auto');
        el.style.cssText = 'width:100%;';
        return el;
      },
      onEvent({ elInput, component }) { setMobileFs(component, elInput.value); },
      onUpdate({ elInput, component }) { elInput.value = mobileFsValue(component); },
    });

    // ---- attach traits on selection (idempotent) ---------------------------
    const KEEP_INLINE_TRAIT = {
      type: 'mautic-keep-inline',
      name: 'mauticKeepInline',
      label: label('grapesjsbuilder.keepInline', 'Keep side-by-side on mobile'),
    };
    const REVERSE_TRAIT = {
      type: 'checkbox',
      name: 'direction',
      label: label('grapesjsbuilder.reverseMobile', 'Reverse order on mobile'),
      valueTrue: 'rtl',
      valueFalse: 'ltr',
    };
    const SHOW_ON_TRAIT = {
      type: 'mautic-show-on',
      name: 'mauticShowOn',
      label: label('grapesjsbuilder.showOn', 'Show on'),
    };
    // Native MJML attribute — makes the image span the full column width on mobile.
    const FULLWIDTH_TRAIT = {
      type: 'checkbox',
      name: 'fluid-on-mobile',
      label: label('grapesjsbuilder.fullWidthMobile', 'Full width on mobile'),
      valueTrue: 'true',
      valueFalse: '',
    };
    const MOBILE_FS_TRAIT = {
      type: 'mautic-mobile-fs',
      name: 'mauticMobileFs',
      label: label('grapesjsbuilder.mobileFontSize', 'Mobile font size (px)'),
    };
    const addTraitOnce = (comp, def) => {
      if (!comp.getTrait || !comp.getTrait(def.name)) comp.addTrait(def);
    };

    editor.on('component:selected', (component) => {
      if (!component) return;
      try {
        if (typeIs(component, 'mj-section')) {
          addTraitOnce(component, KEEP_INLINE_TRAIT);
          addTraitOnce(component, REVERSE_TRAIT);
          addTraitOnce(component, SHOW_ON_TRAIT);
        } else {
          const showable = ['mj-column', 'mj-image', 'mj-text', 'mj-button', 'mj-hero'];
          if (showable.some((n) => typeIs(component, n))) addTraitOnce(component, SHOW_ON_TRAIT);
          if (typeIs(component, 'mj-image')) addTraitOnce(component, FULLWIDTH_TRAIT);
          if (typeIs(component, 'mj-text') || typeIs(component, 'mj-button')) {
            addTraitOnce(component, MOBILE_FS_TRAIT);
          }
        }
      } catch (error) {
        // A trait failure must never break selection/editing.
        console.error('Responsive trait attach failed:', error);
      }
    });

    // ---- live dark-mode preview (canvas approximation) ---------------------
    // Toggles a class on the canvas <body> that inverts the email and re-inverts
    // images — the standard, clearly-approximate way to sanity-check dark mode in
    // an editor (real client dark-mode rendering varies). Preview only; nothing is
    // written to the email content.
    const DARK_CLASS = 'mautic-dark-preview';
    const DARK_STYLE =
      `.${DARK_CLASS}{filter:invert(1) hue-rotate(180deg);background:#ffffff!important;}` +
      `.${DARK_CLASS} img{filter:invert(1) hue-rotate(180deg);}`;
    const setDarkPreview = (on) => {
      try {
        const doc = editor.Canvas.getDocument();
        const body = editor.Canvas.getBody();
        if (!doc || !body) return;
        if (!doc.getElementById('mautic-dark-preview-style')) {
          const st = doc.createElement('style');
          st.id = 'mautic-dark-preview-style';
          st.textContent = DARK_STYLE;
          (doc.head || doc.documentElement).appendChild(st);
        }
        body.classList.toggle(DARK_CLASS, on);
      } catch (error) {
        console.error('Dark-mode preview toggle failed:', error);
      }
    };
    editor.Commands.add('mautic-dark-preview', {
      run() { setDarkPreview(true); },
      stop() { setDarkPreview(false); },
    });
    if (editor.Panels.getButton('options', 'mautic-dark-preview') === null) {
      editor.Panels.addButton('options', {
        id: 'mautic-dark-preview',
        className: 'fa fa-moon-o',
        command: 'mautic-dark-preview',
        attributes: { title: label('grapesjsbuilder.darkPreview', 'Toggle dark-mode preview') },
        active: false,
      });
    }
  }

  /**
   * Shared floating panel (Brand Kit / Saved Blocks / Smart Check). Returns
   * { root, body, close } appended over the builder. Only one is shown at a time.
   */
  static uiFloating(title, opts = {}) {
    document.querySelectorAll('.mautic-ui-floating').forEach((e) => e.remove());
    const container = document.querySelector('.builder') || document.body;
    const root = document.createElement('div');
    root.className = 'mautic-ui-floating';
    root.setAttribute('role', 'dialog');
    root.style.cssText = [
      'position:absolute', `top:${opts.top || 60}px`, 'right:16px', 'z-index:100001',
      'width:300px', 'max-width:90vw', 'background:#20232a', 'color:#e6e8ef',
      'border:1px solid #3a3f4b', 'border-radius:10px', 'box-shadow:0 8px 30px rgba(0,0,0,.4)',
      'font-size:13px', 'font-family:Arial,Helvetica,sans-serif', 'overflow:hidden',
    ].join(';');
    const head = document.createElement('div');
    head.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;padding:10px 12px;background:#171a20;border-bottom:1px solid #3a3f4b;font-weight:600;';
    const h = document.createElement('span');
    h.textContent = title;
    const x = document.createElement('button');
    x.type = 'button';
    x.textContent = '×';
    x.setAttribute('aria-label', 'Close');
    x.style.cssText = 'border:none;background:none;color:#e6e8ef;font-size:18px;line-height:1;cursor:pointer;';
    head.append(h, x);
    const body = document.createElement('div');
    body.style.cssText = 'padding:12px;max-height:60vh;overflow:auto;';
    root.append(head, body);
    container.appendChild(root);
    const close = () => root.remove();
    x.addEventListener('click', close);
    return { root, body, close };
  }

  /** Transient confirmation toast over the builder. */
  static uiToast(msg) {
    const container = document.querySelector('.builder') || document.body;
    const t = document.createElement('div');
    t.setAttribute('role', 'status');
    t.textContent = msg;
    t.style.cssText =
      'position:absolute;bottom:24px;left:50%;transform:translateX(-50%);z-index:100002;background:#2e7d32;color:#fff;padding:9px 16px;border-radius:6px;font:13px Arial,Helvetica,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.3);';
    container.appendChild(t);
    setTimeout(() => t.remove(), 2200);
  }

  /**
   * Brand Kit — global fonts/colours applied once as MJML `<mj-attributes>`
   * defaults (+ a link-colour `<mj-style>`) in `<mj-head>`, so new content
   * inherits the brand and a palette change updates everywhere. Components with
   * their own explicit styles keep them (mj-attributes only sets defaults). The
   * values live in the email content, so they persist and export normally.
   */
  static addBrandKit(editor) {
    if (!editor) return;
    const label = (k, f) => { const s = Mautic.translate(k); return !s || s === k ? f : s; };
    const typeIs = (c, n) => c.get('type') === n || c.get('tagName') === n;
    const kids = (c) => (c && c.components() ? c.components().models : []);

    const getHead = () => {
      const root = editor.getWrapper();
      let head = null;
      const walk = (c) => { if (!c || head) return; if (typeIs(c, 'mj-head')) { head = c; return; } kids(c).forEach(walk); };
      walk(root);
      if (!head) {
        const mjmlRoot = typeIs(root, 'mjml') ? root : kids(root).find((c) => typeIs(c, 'mjml')) || root;
        const a = mjmlRoot.append('<mj-head></mj-head>', { at: 0 });
        head = Array.isArray(a) ? a[0] : a;
      }
      return head;
    };

    const FONTS = [
      ['', label('grapesjsbuilder.brandFontDefault', 'Theme default')],
      ['Arial, Helvetica, sans-serif', 'Arial'],
      ['Georgia, serif', 'Georgia'],
      ["'Times New Roman', Times, serif", 'Times New Roman'],
      ['Verdana, Geneva, sans-serif', 'Verdana'],
      ['Tahoma, Geneva, sans-serif', 'Tahoma'],
      ["'Trebuchet MS', Helvetica, sans-serif", 'Trebuchet MS'],
      ["'Courier New', monospace", 'Courier New'],
    ];

    const readState = () => {
      const st = { font: '', textColor: '', linkColor: '', btnBg: '', btnColor: '' };
      kids(getHead()).forEach((c) => {
        if (typeIs(c, 'mj-attributes')) {
          kids(c).forEach((a) => {
            const at = a.getAttributes();
            if (typeIs(a, 'mj-all') && at['font-family']) st.font = at['font-family'];
            if (typeIs(a, 'mj-text')) { if (at.color) st.textColor = at.color; if (at['font-family']) st.font = st.font || at['font-family']; }
            if (typeIs(a, 'mj-button')) { if (at['background-color']) st.btnBg = at['background-color']; if (at.color) st.btnColor = at.color; }
          });
        }
        const h = c.toHTML() || '';
        if (h.includes('mautic-brand-link')) { const m = h.match(/color:(#[0-9a-fA-F]{3,8})/); if (m) st.linkColor = m[1]; }
      });
      return st;
    };

    const apply = (st) => {
      const head = getHead();
      const attrsNode = kids(head).find((c) => typeIs(c, 'mj-attributes'));

      if (!attrsNode) {
        // No existing defaults to preserve — safe to create a fresh <mj-attributes>
        // in one shot (a blank email has no mj-class definitions to lose).
        const rows = [];
        if (st.font) rows.push(`<mj-all font-family="${st.font}" />`);
        const t = [];
        if (st.font) t.push(`font-family="${st.font}"`);
        if (st.textColor) t.push(`color="${st.textColor}"`);
        if (t.length) rows.push(`<mj-text ${t.join(' ')} />`);
        const b = [];
        if (st.btnBg) b.push(`background-color="${st.btnBg}"`);
        if (st.btnColor) b.push(`color="${st.btnColor}"`);
        if (st.font) b.push(`font-family="${st.font}"`);
        if (b.length) rows.push(`<mj-button ${b.join(' ')} />`);
        if (rows.length) head.append(`<mj-attributes>${rows.join('')}</mj-attributes>`);
      } else {
        // CRITICAL: an existing <mj-attributes> may carry the template's <mj-class>
        // definitions (e.g. header/item) that body components depend on. Removing or
        // rebuilding it — or appending children, which makes grapesjs-mjml re-parse
        // and DROP those mj-class nodes — breaks the whole email. So update the
        // managed children IN PLACE only, and never touch anything else.
        const updateChild = (tag, updates) => {
          if (!Object.keys(updates).length) return;
          const node = kids(attrsNode).find((c) => typeIs(c, tag));
          if (node) node.addAttributes(updates);
        };
        if (st.font) updateChild('mj-all', { 'font-family': st.font });
        const tUpd = {};
        if (st.font) tUpd['font-family'] = st.font;
        if (st.textColor) tUpd.color = st.textColor;
        updateChild('mj-text', tUpd);
        const bUpd = {};
        if (st.btnBg) bUpd['background-color'] = st.btnBg;
        if (st.btnColor) bUpd.color = st.btnColor;
        if (st.font) bUpd['font-family'] = st.font;
        updateChild('mj-button', bUpd);
      }

      // Link colour lives in our own marked <mj-style> on <mj-head> (a sibling of
      // <mj-attributes>, so this never disturbs it) — replace only that node.
      kids(head).slice().forEach((c) => { if ((c.toHTML() || '').includes('mautic-brand-link')) c.remove(); });
      if (st.linkColor) head.append(`<mj-style>/*mautic-brand-link*/a,a:visited{color:${st.linkColor} !important;}</mj-style>`);
    };

    const open = () => {
      const st = readState();
      const { body } = BuilderService.uiFloating(label('grapesjsbuilder.brandKit', 'Brand Kit'));
      const addRow = (text, inputEl) => {
        const row = document.createElement('label');
        row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:12px;';
        const s = document.createElement('span');
        s.textContent = text;
        row.append(s, inputEl);
        body.appendChild(row);
      };
      const fontSel = document.createElement('select');
      fontSel.style.cssText = 'flex:1;max-width:170px;';
      FONTS.forEach(([v, name]) => { const o = document.createElement('option'); o.value = v; o.textContent = name; if (v === st.font) o.selected = true; fontSel.appendChild(o); });
      fontSel.addEventListener('change', () => { st.font = fontSel.value; apply(st); });
      addRow(label('grapesjsbuilder.brandFont', 'Body font'), fontSel);
      const colorInput = (val, onch) => {
        const i = document.createElement('input');
        i.type = 'color';
        if (val) i.value = val;
        i.style.cssText = 'width:40px;height:28px;border:none;background:none;cursor:pointer;';
        i.addEventListener('input', () => onch(i.value));
        return i;
      };
      addRow(label('grapesjsbuilder.brandTextColor', 'Text color'), colorInput(st.textColor || '#333333', (v) => { st.textColor = v; apply(st); }));
      addRow(label('grapesjsbuilder.brandLinkColor', 'Link color'), colorInput(st.linkColor || '#1a73e8', (v) => { st.linkColor = v; apply(st); }));
      addRow(label('grapesjsbuilder.brandBtnBg', 'Button color'), colorInput(st.btnBg || '#4e5d9d', (v) => { st.btnBg = v; apply(st); }));
      addRow(label('grapesjsbuilder.brandBtnText', 'Button text'), colorInput(st.btnColor || '#ffffff', (v) => { st.btnColor = v; apply(st); }));
      const hint = document.createElement('div');
      hint.style.cssText = 'font-size:11px;color:#9aa0ac;margin-top:4px;';
      hint.textContent = label('grapesjsbuilder.brandHint', 'Applied as defaults; components with their own styles keep them.');
      body.appendChild(hint);
    };

    editor.Commands.add('mautic-brand-kit', { run() { open(); } });
    if (editor.Panels.getButton('options', 'mautic-brand-kit') === null) {
      editor.Panels.addButton('options', {
        id: 'mautic-brand-kit',
        className: 'fa fa-paint-brush',
        command: 'mautic-brand-kit',
        attributes: { title: label('grapesjsbuilder.brandKit', 'Brand Kit') },
      });
    }
  }

  /**
   * Saved Blocks — save the selected section/column/hero as a reusable block that
   * reappears in the "Saved" category of the Blocks panel. Persisted in the
   * browser (localStorage); a fully synced/shared model would need server storage.
   */
  static addSavedBlocks(editor) {
    if (!editor) return;
    const label = (k, f) => { const s = Mautic.translate(k); return !s || s === k ? f : s; };
    const KEY = 'mauticSavedBlocks';
    const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; } };
    const store = (arr) => { try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch (e) { /* storage may be unavailable */ } };
    const cat = label('grapesjsbuilder.savedCategory', 'Saved');
    const register = (b) => { try { editor.BlockManager.add(b.id, { label: b.label, category: cat, content: b.content, attributes: { class: 'fa fa-bookmark' } }); } catch (e) { /* ignore bad saved entry */ } };
    load().forEach(register);

    const askName = (cb) => {
      const { body, close } = BuilderService.uiFloating(label('grapesjsbuilder.saveBlockTitle', 'Save block'));
      const inp = document.createElement('input');
      inp.type = 'text';
      inp.placeholder = label('grapesjsbuilder.saveBlockName', 'Block name');
      inp.style.cssText = 'width:100%;padding:7px;margin-bottom:10px;box-sizing:border-box;';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = label('grapesjsbuilder.save', 'Save');
      btn.style.cssText = 'padding:7px 14px;background:#4e5d9d;color:#fff;border:none;border-radius:5px;cursor:pointer;';
      const done = () => { const v = inp.value.trim(); if (v) { close(); cb(v); } };
      btn.addEventListener('click', done);
      inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') done(); });
      body.append(inp, btn);
      setTimeout(() => inp.focus(), 30);
    };

    editor.Commands.add('mautic-save-block', {
      run(ed) {
        const c = ed.getSelected();
        if (!c) return;
        const content = c.toHTML();
        askName((name) => {
          const b = { id: `saved-${Date.now()}`, label: name, content };
          const arr = load();
          arr.push(b);
          store(arr);
          register(b);
          BuilderService.uiToast(label('grapesjsbuilder.savedToast', 'Saved to “Saved” blocks'));
        });
      },
    });

    editor.on('component:selected', (c) => {
      try {
        if (!c) return;
        const ok = ['mj-section', 'mj-column', 'mj-hero'].some((n) => c.get('type') === n || c.get('tagName') === n);
        if (!ok) return;
        const tb = c.get('toolbar') || [];
        if (!tb.some((x) => x.command === 'mautic-save-block')) {
          tb.push({
            attributes: { class: 'fa fa-bookmark', title: label('grapesjsbuilder.saveBlock', 'Save as block') },
            command: 'mautic-save-block',
          });
          c.set('toolbar', tb);
        }
      } catch (e) {
        // Adding a toolbar button must never break selection.
      }
    });
  }

  /**
   * Smart Check — an in-builder accessibility/deliverability linter. Warn-only;
   * each finding jumps to the offending component. Runs entirely client-side.
   */
  static addSmartCheck(editor) {
    if (!editor) return;
    const label = (k, f) => { const s = Mautic.translate(k); return !s || s === k ? f : s; };
    const typeIs = (c, n) => c.get('type') === n || c.get('tagName') === n;
    const kids = (c) => (c && c.components() ? c.components().models : []);

    const run = () => {
      const findings = [];
      const visit = (c) => {
        if (!c) return;
        const at = c.getAttributes ? c.getAttributes() : {};
        if (typeIs(c, 'mj-image')) {
          const src = c.get('src') || at.src || '';
          if (!at.alt || !String(at.alt).trim()) findings.push({ c, msg: label('grapesjsbuilder.checkAlt', 'Image is missing alt text') });
          if (!src || src.startsWith('data:image/svg+xml')) findings.push({ c, msg: label('grapesjsbuilder.checkPlaceholder', 'Image still uses a placeholder / has no source') });
          const w = parseInt(at.width, 10);
          if (w && w > 600) findings.push({ c, msg: label('grapesjsbuilder.checkWideImage', 'Image is wider than the 600px email body') });
        }
        if (typeIs(c, 'mj-button')) {
          const href = at.href || '';
          if (!href || href === '#' || href === 'https://') findings.push({ c, msg: label('grapesjsbuilder.checkBtnHref', 'Button has no link') });
        }
        kids(c).forEach(visit);
      };
      visit(editor.getWrapper());
      const html = (editor.getHtml && editor.getHtml()) || '';
      if (!/unsubscribe|\{unsubscribe/i.test(html)) findings.push({ c: null, msg: label('grapesjsbuilder.checkUnsub', 'No unsubscribe link found') });
      // Litmus-style dark-mode safety nudge.
      if (!/color-scheme/i.test(html)) findings.push({ c: null, msg: label('grapesjsbuilder.checkDarkMode', 'No dark-mode support declared (add a color-scheme meta/style)') });

      const { body } = BuilderService.uiFloating(label('grapesjsbuilder.smartCheck', 'Smart Check'));
      if (!findings.length) {
        const ok = document.createElement('div');
        ok.style.cssText = 'color:#7bd88f;padding:6px 0;';
        ok.textContent = label('grapesjsbuilder.checkPass', 'No issues found ✓');
        body.appendChild(ok);
        return;
      }
      const count = document.createElement('div');
      count.style.cssText = 'margin-bottom:10px;color:#f0c36d;font-weight:600;';
      count.textContent = `${findings.length} ${label('grapesjsbuilder.checkIssues', 'issue(s) to review')}`;
      body.appendChild(count);
      findings.forEach((f) => {
        const row = document.createElement('div');
        row.style.cssText = `padding:8px;border:1px solid #3a3f4b;border-radius:6px;margin-bottom:8px;${f.c ? 'cursor:pointer;' : ''}`;
        row.textContent = f.msg;
        if (f.c) {
          row.addEventListener('click', () => {
            editor.select(f.c);
            try { if (f.c.view && f.c.view.el) f.c.view.el.scrollIntoView({ block: 'center' }); } catch (e) { /* noop */ }
          });
        }
        body.appendChild(row);
      });
      const hint = document.createElement('div');
      hint.style.cssText = 'font-size:11px;color:#9aa0ac;margin-top:4px;';
      hint.textContent = label('grapesjsbuilder.checkHint', 'Warnings only — click an item to jump to it.');
      body.appendChild(hint);
    };

    editor.Commands.add('mautic-smart-check', { run() { run(); } });
    if (editor.Panels.getButton('options', 'mautic-smart-check') === null) {
      editor.Panels.addButton('options', {
        id: 'mautic-smart-check',
        className: 'fa fa-check-circle-o',
        command: 'mautic-smart-check',
        attributes: { title: label('grapesjsbuilder.smartCheck', 'Smart Check (accessibility & deliverability)') },
      });
    }
  }

  /**
   * AI "Rewrite" — a per-text-block toolbar action that sends the block's text to
   * the email AI endpoint (Claude via Portkey) and offers the rewritten copy for
   * review before it replaces the block. Non-destructive: the marketer sees the
   * suggestion and clicks "Replace" (or Cancel) — nothing changes on its own.
   *
   * Graceful degradation: if the instance has no AI configured
   * (`window.mauticEmailAiEnabled` is set by the email form only when a key is
   * saved), the whole feature registers nothing — no command, no toolbar button —
   * so the builder is unchanged when AI is off.
   */
  static addAiRewrite(editor) {
    if (!editor || !window.mauticEmailAiEnabled) return;
    const label = (k, f) => { const s = Mautic.translate(k); return !s || s === k ? f : s; };
    const url = window.mauticEmailAiUrl || `${mauticBaseUrl}s/emails/ai/generate`;

    // Text-ish components that make sense to rewrite (mj-text in MJML mode, plain
    // text in HTML mode). Deliberately excludes images/buttons/structure.
    const isTextBlock = (c) => {
      if (!c) return false;
      const t = c.get('type');
      const tag = c.get('tagName');
      return t === 'text' || t === 'mj-text' || tag === 'mj-text';
    };

    // HTML-escape then keep line breaks, for safe write-back into the component.
    const toSafeHtml = (txt) => {
      const div = document.createElement('div');
      div.textContent = txt;
      return div.innerHTML.replace(/\r?\n/g, '<br>');
    };

    editor.Commands.add('mautic-ai-rewrite', {
      run(ed) {
        const c = ed.getSelected();
        if (!isTextBlock(c)) return;
        const el = c.getEl();
        const original = ((el && el.innerText) || '').replace(/\s+\n/g, '\n').trim();

        const { body, close } = BuilderService.uiFloating(label('grapesjsbuilder.aiRewrite', 'AI rewrite'));
        if (!original) {
          const empty = document.createElement('div');
          empty.style.cssText = 'color:#f0c36d;padding:6px 0;';
          empty.textContent = label('grapesjsbuilder.aiRewriteEmpty', 'This block has no text to rewrite.');
          body.appendChild(empty);
          return;
        }

        const status = document.createElement('div');
        status.style.cssText = 'color:#9aa0ac;padding:4px 0 10px;';
        status.textContent = label('grapesjsbuilder.aiRewriting', 'Rewriting…');
        body.appendChild(status);

        mQuery.ajax({
          url,
          type: 'POST',
          data: { task: 'rewrite', text: original.slice(0, 4000) },
        }).done((res) => {
          if (!res || !res.text) {
            status.style.color = '#e57373';
            status.textContent = (res && res.error) ? res.error : label('grapesjsbuilder.aiRewriteFailed', 'Could not rewrite the text.');
            return;
          }
          status.remove();
          const out = document.createElement('div');
          out.style.cssText = 'white-space:pre-wrap;background:#171a20;border:1px solid #3a3f4b;border-radius:6px;padding:10px;margin-bottom:10px;max-height:38vh;overflow:auto;';
          out.textContent = res.text;
          body.appendChild(out);

          const actions = document.createElement('div');
          actions.style.cssText = 'display:flex;gap:8px;';
          const replace = document.createElement('button');
          replace.type = 'button';
          replace.textContent = label('grapesjsbuilder.aiRewriteReplace', 'Replace text');
          replace.style.cssText = 'padding:7px 14px;background:#4e5d9d;color:#fff;border:none;border-radius:5px;cursor:pointer;';
          replace.addEventListener('click', () => {
            try {
              c.components(toSafeHtml(res.text));
              BuilderService.uiToast(label('grapesjsbuilder.aiRewriteApplied', 'Text updated'));
            } catch (e) {
              // Write-back must never break the canvas.
            }
            close();
          });
          const cancel = document.createElement('button');
          cancel.type = 'button';
          cancel.textContent = label('grapesjsbuilder.cancel', 'Cancel');
          cancel.style.cssText = 'padding:7px 14px;background:none;color:#e6e8ef;border:1px solid #3a3f4b;border-radius:5px;cursor:pointer;';
          cancel.addEventListener('click', close);
          actions.append(replace, cancel);
          body.appendChild(actions);

          const hint = document.createElement('div');
          hint.style.cssText = 'font-size:11px;color:#9aa0ac;margin-top:8px;';
          hint.textContent = label('grapesjsbuilder.aiRewriteHint', 'Review before replacing — inline links/formatting are not preserved.');
          body.appendChild(hint);
        }).fail((xhr) => {
          status.style.color = '#e57373';
          let msg = label('grapesjsbuilder.aiRewriteFailed', 'Could not rewrite the text.');
          try { const j = JSON.parse(xhr.responseText); if (j && j.error) msg = j.error; } catch (e) { /* keep default */ }
          status.textContent = msg;
        });
      },
    });

    editor.on('component:selected', (c) => {
      try {
        if (!isTextBlock(c)) return;
        const tb = c.get('toolbar') || [];
        if (!tb.some((x) => x.command === 'mautic-ai-rewrite')) {
          tb.push({
            attributes: { class: 'fa fa-magic', title: label('grapesjsbuilder.aiRewrite', 'AI rewrite') },
            command: 'mautic-ai-rewrite',
          });
          c.set('toolbar', tb);
        }
      } catch (e) {
        // Adding a toolbar button must never break selection.
      }
    });
  }

  /**
   * Preview text (inbox preheader) editor — the short line inboxes show after the
   * subject. In MJML this is `<mj-preview>` in `<mj-head>`; marketers previously
   * had to hand-edit it in Code Mode. This adds an always-on panel to view/edit it
   * inline, plus (when AI is configured) a one-click "Generate with AI" that fills
   * suggestions from the email's subject + body (task=preheader → Claude).
   *
   * MJML mode only — `<mj-preview>` is an MJML head element honoured by both the
   * Preview microservice and the send engine.
   */
  static addPreheader(editor, mode = 'mjml') {
    if (!editor) return;
    const label = (k, f) => { const s = Mautic.translate(k); return !s || s === k ? f : s; };
    const typeIs = (c, n) => c.get('type') === n || c.get('tagName') === n;
    const kids = (c) => (c && c.components() ? c.components().models : []);
    const esc = (t) => (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    let findPreview;
    let readPreview;
    let setPreview;

    if ('html' === mode) {
      // HTML / newsletter themes have no <mj-head>; the preheader is the classic
      // visually-hidden <div> at the top of the body. We tag it data-preheader so
      // we can find/update/remove exactly our node and never touch real content.
      const PREHEADER_STYLE = 'display:none;max-height:0;max-width:0;overflow:hidden;'
        + 'mso-hide:all;font-size:1px;line-height:1px;color:#ffffff;opacity:0;';
      findPreview = () => {
        let found = null;
        const walk = (c) => {
          if (!c || found) return;
          const attrs = c.getAttributes ? c.getAttributes() : {};
          if (attrs && undefined !== attrs['data-preheader']) { found = c; return; }
          kids(c).forEach(walk);
        };
        walk(editor.getWrapper());
        return found;
      };
      readPreview = () => {
        const p = findPreview();
        if (!p) return '';
        // Content is escaped plain text — strip the wrapping <div> to get it back.
        return (p.toHTML() || '').replace(/<\/?div[^>]*>/gi, '').trim();
      };
      setPreview = (text) => {
        const val = (text || '').trim();
        const existing = findPreview();
        if (!val) { if (existing) existing.remove(); return; }
        if (existing) existing.components(esc(val));
        else editor.getWrapper().append(`<div data-preheader="1" style="${PREHEADER_STYLE}">${esc(val)}</div>`, { at: 0 });
      };
    } else {
      // Find or create <mj-head> (same approach as Brand Kit).
      const getHead = () => {
        const root = editor.getWrapper();
        let head = null;
        const walk = (c) => { if (!c || head) return; if (typeIs(c, 'mj-head')) { head = c; return; } kids(c).forEach(walk); };
        walk(root);
        if (!head) {
          const mjmlRoot = typeIs(root, 'mjml') ? root : kids(root).find((c) => typeIs(c, 'mjml')) || root;
          const a = mjmlRoot.append('<mj-head></mj-head>', { at: 0 });
          head = Array.isArray(a) ? a[0] : a;
        }
        return head;
      };
      findPreview = () => kids(getHead()).find((c) => typeIs(c, 'mj-preview')) || null;
      readPreview = () => {
        const p = findPreview();
        if (!p) return '';
        return (p.toHTML() || '').replace(/<\/?mj-preview[^>]*>/gi, '').trim();
      };
      setPreview = (text) => {
        const head = getHead();
        const val = (text || '').trim();
        const existing = findPreview();
        if (!val) { if (existing) existing.remove(); return; }
        if (existing) existing.components(esc(val));
        else head.append(`<mj-preview>${esc(val)}</mj-preview>`);
      };
    }

    const open = () => {
      const { body } = BuilderService.uiFloating(label('grapesjsbuilder.preheader', 'Preview text'));
      const intro = document.createElement('div');
      intro.style.cssText = 'font-size:11px;color:#9aa0ac;margin-bottom:8px;';
      intro.textContent = label('grapesjsbuilder.preheaderIntro', 'The short line inboxes show after the subject.');
      body.appendChild(intro);

      const ta = document.createElement('textarea');
      ta.value = readPreview();
      ta.rows = 3;
      ta.style.cssText = 'width:100%;box-sizing:border-box;background:#171a20;color:#e6e8ef;border:1px solid #3a3f4b;border-radius:6px;padding:8px;resize:vertical;';
      body.appendChild(ta);

      const counter = document.createElement('div');
      counter.style.cssText = 'font-size:11px;color:#9aa0ac;margin:4px 0 10px;text-align:right;';
      const updateCount = () => {
        const n = ta.value.trim().length;
        counter.textContent = `${n} ${label('grapesjsbuilder.preheaderChars', 'chars')}${n > 140 ? ' — ' + label('grapesjsbuilder.preheaderLong', 'many inboxes truncate ~90–140') : ''}`;
      };
      ta.addEventListener('input', updateCount);
      updateCount();
      body.appendChild(counter);

      // AI suggestions (only when configured).
      if (window.mauticEmailAiEnabled) {
        const gen = document.createElement('button');
        gen.type = 'button';
        const genIcon = document.createElement('i');
        genIcon.className = 'fa fa-magic';
        const genLabel = document.createElement('span');
        genLabel.textContent = ' ' + label('grapesjsbuilder.preheaderGenerate', 'Generate with AI');
        gen.append(genIcon, genLabel);
        gen.style.cssText = 'padding:7px 12px;background:none;color:#c7d0ff;border:1px solid #4e5d9d;border-radius:5px;cursor:pointer;margin-bottom:10px;';
        const list = document.createElement('div');
        list.style.cssText = 'margin-bottom:10px;';
        gen.addEventListener('click', () => {
          gen.disabled = true;
          const prevLabel = genLabel.textContent;
          genLabel.textContent = ' ' + label('grapesjsbuilder.preheaderGenerating', 'Generating…');
          list.textContent = '';
          const subject = (document.getElementById('emailform_subject') || {}).value || '';
          const name = (document.getElementById('emailform_name') || {}).value || '';
          const bodyText = ((editor.getWrapper().getEl() || {}).innerText || '').replace(/\s+/g, ' ').trim().slice(0, 3000);
          mQuery.ajax({
            url: window.mauticEmailAiUrl, type: 'POST',
            data: { task: 'preheader', subject, emailName: name, bodyText },
          }).done((res) => {
            const items = (res && res.suggestions) || [];
            if (!items.length) {
              list.textContent = (res && res.error) || label('grapesjsbuilder.preheaderNone', 'No suggestions returned.');
              return;
            }
            items.forEach((s) => {
              const a = document.createElement('a');
              a.href = '#';
              a.textContent = s;
              a.style.cssText = 'display:block;padding:7px 9px;border:1px solid #3a3f4b;border-radius:6px;margin-bottom:6px;color:#e6e8ef;text-decoration:none;';
              a.addEventListener('click', (e) => { e.preventDefault(); ta.value = s; updateCount(); ta.focus(); });
              list.appendChild(a);
            });
          }).fail((xhr) => {
            let msg = label('grapesjsbuilder.preheaderFailed', 'Could not generate suggestions.');
            try { const j = JSON.parse(xhr.responseText); if (j && j.error) msg = j.error; } catch (e) { /* keep default */ }
            list.textContent = msg;
          }).always(() => { gen.disabled = false; genLabel.textContent = prevLabel; });
        });
        body.append(gen, list);
      }

      const save = document.createElement('button');
      save.type = 'button';
      save.textContent = label('grapesjsbuilder.save', 'Save');
      save.style.cssText = 'padding:7px 14px;background:#4e5d9d;color:#fff;border:none;border-radius:5px;cursor:pointer;';
      save.addEventListener('click', () => {
        try {
          setPreview(ta.value);
          BuilderService.uiToast(label('grapesjsbuilder.preheaderSaved', 'Preview text updated'));
        } catch (e) {
          // Writing the head must never break the canvas.
        }
      });
      body.appendChild(save);
    };

    editor.Commands.add('mautic-preheader', { run() { open(); } });
    if (editor.Panels.getButton('options', 'mautic-preheader') === null) {
      editor.Panels.addButton('options', {
        id: 'mautic-preheader',
        className: 'fa fa-eye',
        command: 'mautic-preheader',
        attributes: { title: label('grapesjsbuilder.preheader', 'Preview text (inbox preheader)') },
      });
    }
  }

  initEmailHtml() {
    const components = contentService.getOriginalContentHtml().body.innerHTML;
    if (!components) {
      throw new Error('no components');
    }

    const styles = [
      `${mauticBaseUrl}plugins/GrapesJsBuilderBundle/Assets/library/js/grapesjs-editor.css`
    ];

    // Launch GrapesJS with body part
    this.editor = grapesjs.init({
      clearOnRender: true,
      container: '.builder-panel',
      components,
      height: '100%',
      canvas: {
        styles,
      },
      storageManager: false,
      assetManager: this.getAssetManagerConf(),
      styleManager: BuilderService.getStyleManagerConf(),
      layerManager: {
        appendTo: '.gjs-pn-views-container',
      },
      // CKEditor plugin removed - using native GrapesJS inline editing
      plugins: [grapesjsnewsletter, grapesjspostcss, grapesjsmautic],
      pluginsOpts: {
        grapesjsnewsletter: {},
        grapesjsmautic: BuilderService.getMauticConf('email-html'),
      },
    });

    // add a Mautic custom block Button
    this.editor.BlockManager.get('button').set({
      content:
        '<a href="#" target="_blank" style="display:inline-block;text-decoration:none;border-color:#4e5d9d;border-width: 10px 20px;border-style:solid; text-decoration: none; -webkit-border-radius: 3px; -moz-border-radius: 3px; border-radius: 3px; background-color: #4e5d9d; display: inline-block;font-size: 16px; color: #ffffff; ">\n' +
        'Button\n' +
        '</a>',
    });

    // AI "Rewrite" action on text blocks — no-op unless AI is configured.
    BuilderService.addAiRewrite(this.editor);

    // Preview-text (inbox preheader) editor — HTML mode uses a hidden <div>.
    BuilderService.addPreheader(this.editor, 'html');

    // Fix Layer Manager: Ensure it's properly initialized and rendered
    BuilderService.initializeLayerManager(this.editor);

    return this.editor;
  }

  /**
   * Manage button loading indicator
   *
   * @param activate - true or false
   */
  static setupButtonLoadingIndicator(activate) {
    const builderButton = mQuery('.btn-builder');
    const saveButton = mQuery('.btn-save');
    const applyButton = mQuery('.btn-apply');

    if (activate) {
      Mautic.activateButtonLoadingIndicator(builderButton);
      Mautic.activateButtonLoadingIndicator(saveButton);
      Mautic.activateButtonLoadingIndicator(applyButton);
    } else {
      Mautic.removeButtonLoadingIndicator(builderButton);
      Mautic.removeButtonLoadingIndicator(saveButton);
      Mautic.removeButtonLoadingIndicator(applyButton);
    }
  }

  /**
   * Configure the Asset Manager for all modes
   * @link https://grapesjs.com/docs/modules/Assets.html#configuration
   */
  getAssetManagerConf() {
    return {
      assets: this.assets,
      noAssets: Mautic.translate('grapesjsbuilder.assetManager.noAssets'),
      upload: this.uploadPath,
      uploadName: 'files',
      multiUpload: 1,
      embedAsBase64: false,
      openAssetsOnDrop: 1,
      autoAdd: 1,
      headers: { 'X-CSRF-Token': mauticAjaxCsrf }, // global variable
    };
  }

  getEditor() {
    return this.editor;
  }

  overrideCustomRteDisable() {
    const richTextEditor = this.editor.RichTextEditor;

    if (!richTextEditor) {
      console.error('No RichTextEditor found');
      return;
    }

    if (richTextEditor.customRte) {
      richTextEditor.customRte.disable = (el, rte) => {
        el.contentEditable = false;
        if (rte && rte.focusManager) {
          rte.focusManager.blur(true);
        }

        if (rte && typeof rte.destroy == 'function') {
          rte.destroy();
        }
      };
    }
  }
  /**
   * Generate assets list from GrapesJs
   */
  // getAssetsList() {
  //   const assetManager = this.editor.AssetManager;
  //   const assets = assetManager.getAll();
  //   const assetsList = [];

  //   assets.forEach((asset) => {
  //     if (asset.get('type') === 'image') {
  //       assetsList.push({
  //         src: asset.get('src'),
  //         width: asset.get('width'),
  //         height: asset.get('height'),
  //       });
  //     } else {
  //       assetsList.push(asset.get('src'));
  //     }
  //   });

  //   return assetsList;
  // }
}
